/**
 * Загружает RequireJS-зависимость по имени.
 *
 * Используется через `require("OK/RequireJSModuleResolver!%yourModuleName%")`
 */
function load(url: string, req: Function, onload: { error: (e: Error) => void }) {
    if (!document.head && !document.body) {
        throw new Error('DOM must be loaded');
    }

    req([url], onload, function (err: Error) {
        if (onload.error) {
            onload.error(err);
        }
    });
}

type LoadWithRetryParams = {
    /**
     * Максимальное количество попыток перезапросить модули.
     */
    maxAttemptsCount?: number;
    /**
     * Номер текущей попытки.
     */
    attemptNumber?: number,
}

export interface LoadingError extends Error {
    requireType: string;
    requireModules?: string[];
    originalError?: Error;
}

/**
 * Загружает список RequireJS-модулей с повторным перезапросом, если часть модулей не загрузилась.
 *
 * @param modules Список запрашиваемых модулей
 * @param params Опциональные параметры
 */
function loadWithRetry(modules: string[], params: LoadWithRetryParams = {}): Promise<unknown> {
    const retryRequireLoad = params.maxAttemptsCount || 1;
    const attemptNumber = params.attemptNumber || 0;
    const canRetry = attemptNumber <= retryRequireLoad;

    return new Promise((resolve, reject) => {
        return window.require(modules, resolve, (e: LoadingError) => {
            const failedModules = e.requireModules;

            if (failedModules) {
                failedModules.forEach(failedModuleId => {
                    window.require.undef(failedModuleId);
                });
            }

            if (!failedModules || !canRetry) {
                return reject(e);
            }

            // Модули, которые не загрузились, загружаем ещё раз.
            return loadWithRetry(failedModules, Object.assign({}, params, {
                attemptNumber: attemptNumber + 1,
            }))
                .then(modules => resolve(modules))
                .catch(error => reject(error));
        });
    });
}

export {
    load,
    loadWithRetry,
}
